import type { ITemplateRepository } from "@/application/ports/repositories";
import type {
	Template,
	TemplateDetail,
	PaginatedTemplates,
	CustomizeTemplateRequest,
	TemplateUpdateRequest,
	TemplateFilters,
} from "@/domain/template";
import { apiClient } from "../client";
import { getLogger } from "@/infrastructure/di/services";

// Backend response - matches exactly what API returns
interface TemplateListResponseFromAPI {
	templates: Template[];
	total_count: number;
	limit: number;
	offset: number;
}

export class TemplateRepository implements ITemplateRepository {
	async getTemplates(filters?: TemplateFilters): Promise<PaginatedTemplates> {
		const logger = getLogger();
		try {
			const params = new URLSearchParams();
			if (filters?.limit) params.append("limit", filters.limit.toString());
			if (filters?.offset) params.append("offset", filters.offset.toString());
			if (filters?.type) params.append("type", filters.type);
			if (filters?.category) params.append("category", filters.category);
			if (filters?.language) params.append("language", filters.language);
			if (filters?.industry) params.append("industry", filters.industry);

			const queryString = params.toString();
			const url = `/templates${queryString ? `?${queryString}` : ""}`;

			const response = await apiClient.get<TemplateListResponseFromAPI>(url);

			const limit = response.data.limit || 10;
			const offset = response.data.offset || 0;
			const totalCount = response.data.total_count || 0;

			return {
				templates: response.data.templates,
				total_count: totalCount,
				limit: limit,
				offset: offset,
				total_pages: limit > 0 ? Math.ceil(totalCount / limit) : 1,
				current_page: limit > 0 ? Math.floor(offset / limit) + 1 : 1,
				has_next: offset + limit < totalCount,
				has_previous: offset > 0,
			};
		} catch (error) {
			logger.error("Error loading templates:", error);
			throw error;
		}
	}
	async getTemplate(templateUuid: string): Promise<TemplateDetail> {
		try {
			const response = await apiClient.get<TemplateDetail>(
				`/templates/${templateUuid}`,
			);
			return response.data;
		} catch (error) {
			getLogger().error("Error loading template:", error);
			throw error;
		}
	}
	async getTemplateContent(templateUuid: string): Promise<string> {
		try {
			const content = await apiClient.getText(
				`/templates/${templateUuid}/content`,
			);
			return content;
		} catch (error) {
			getLogger().error("Error loading template content:", error);
			throw error;
		}
	}
	async customizeTemplate(
		templateUuid: string,
		request: CustomizeTemplateRequest,
	): Promise<Template> {
		try {
			const response = await apiClient.post<Template>(
				`/templates/${templateUuid}/customize`,
				request,
				{ headers: { "Content-Type": "application/json" } },
			);
			return response.data;
		} catch (error) {
			getLogger().error("Error customizing template:", error);
			throw error;
		}
	}
	async updateTemplate(
		templateUuid: string,
		request: TemplateUpdateRequest,
	): Promise<Template> {
		try {
			const response = await apiClient.patch<Template>(
				`/templates/${templateUuid}`,
				request,
				{
					headers: { "Content-Type": "application/json" },
				},
			);
			return response.data;
		} catch (error) {
			getLogger().error("Error updating template:", error);
			throw error;
		}
	}
	async uploadTemplateContent(
		templateUuid: string,
		file: File,
	): Promise<Template> {
		try {
			const formData = new FormData();
			formData.append("template_file", file);

			const response = await apiClient.post<Template>(
				`/templates/${templateUuid}/upload`,
				formData,
			);
			return response.data;
		} catch (error) {
			getLogger().error("Error uploading template content:", error);
			throw error;
		}
	}
	async deleteTemplateCustomization(templateUuid: string): Promise<void> {
		try {
			await apiClient.delete(`/templates/${templateUuid}`);
		} catch (error) {
			getLogger().error("Error deleting template customization:", error);
			throw error;
		}
	}
}
